import React, { useEffect, useState, useRef } from "react";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Search, ScanLine, Sparkles, UploadCloud, Mail, Phone, Globe, MapPin } from "lucide-react";
import { PageHeader, Badge, EmptyState } from "@/components/Common";

const CLASSIFICATIONS = [
  "supplier", "manufacturer", "importer", "exporter", "government_agency",
  "investor", "retailer", "technology_provider", "logistics", "financial",
  "insurance", "international_partner",
];

const BLANK = {
  full_name: "", title: "", company: "", organization_type: "", country: "", city: "",
  phone: "", mobile: "", whatsapp: "", email: "", website: "",
  industry: "", product_category: "", service_category: "",
  classification: "supplier", event_name: "", meeting_location: "", meeting_date: "",
  notes: "", image_file_id: "",
};

export default function BusinessCards() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState(BLANK);
  const [refreshKey, setRefreshKey] = useState(0);
  const fileRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/business-cards");
        if (!cancelled) setItems(data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);
  const load = () => setRefreshKey((k) => k + 1);

  const handleScan = async (file) => {
    if (!file) return;
    setScanning(true);
    try {
      // Upload
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.message("Running OCR with GPT-5.2 vision…");
      // OCR
      const { data: ocr } = await api.post("/business-cards/ocr", { file_id: up.id });
      setForm((f) => ({
        ...f,
        full_name: ocr.full_name || f.full_name,
        title: ocr.title || f.title,
        company: ocr.company || f.company,
        organization_type: ocr.organization_type || f.organization_type,
        country: ocr.country || f.country,
        city: ocr.city || f.city,
        phone: ocr.phone || f.phone,
        mobile: ocr.mobile || f.mobile,
        whatsapp: ocr.whatsapp || f.whatsapp,
        email: ocr.email || f.email,
        website: ocr.website || f.website,
        industry: ocr.industry || f.industry,
        product_category: ocr.product_category || f.product_category,
        service_category: ocr.service_category || f.service_category,
        image_file_id: up.id,
      }));
      toast.success("Card scanned — review and save");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setScanning(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      await api.post("/business-cards", payload);
      toast.success("Contact saved");
      setOpen(false);
      setForm(BLANK);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this contact?")) return;
    try { await api.delete(`/business-cards/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filtered = items.filter((c) => {
    if (filter !== "all" && c.classification !== filter) return false;
    if (q) {
      const t = `${c.full_name} ${c.company} ${c.country} ${c.industry} ${c.event_name} ${c.email}`.toLowerCase();
      if (!t.includes(q.toLowerCase())) return false;
    }
    return true;
  });

  return (
    <div>
      <PageHeader
        eyebrow="Stakeholders"
        title="Business Card & Strategic Contacts"
        subtitle="Scan business cards from exhibitions, conferences and meetings — auto-extracted with GPT-5.2 vision."
        actions={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm(BLANK); }}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-900 hover:bg-emerald-950 text-white" data-testid="bc-new-button">
                <Plus size={16} className="mr-1.5" /> Add contact
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-2">
                  <Sparkles size={16} className="text-amber-600" />
                  Capture business card
                </DialogTitle>
              </DialogHeader>

              {/* Scan/upload area */}
              <div className="bg-amber-50/40 border border-dashed border-amber-300 rounded-md p-4 mb-4">
                <div className="flex flex-col sm:flex-row items-center gap-3">
                  <div className="flex-1">
                    <div className="font-semibold text-sm text-gray-900 flex items-center gap-1.5">
                      <ScanLine size={14} className="text-emerald-800" />
                      AI Scan business card
                    </div>
                    <div className="text-xs text-gray-600 mt-0.5">
                      Take a clear photo of the card. GPT-5.2 vision will extract contact fields automatically.
                    </div>
                  </div>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => handleScan(e.target.files[0])}
                    data-testid="bc-scan-file"
                  />
                  <Button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={scanning}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="bc-scan-button"
                  >
                    <UploadCloud size={15} className="mr-1.5" />
                    {scanning ? "Scanning…" : "Scan / Upload"}
                  </Button>
                </div>
                {form.image_file_id && (
                  <div className="mt-3">
                    <img src={fileUrl(form.image_file_id)} alt="Card"
                      className="h-32 rounded-md border border-amber-200 object-cover" />
                  </div>
                )}
              </div>

              <form onSubmit={submit} className="space-y-3" data-testid="bc-form">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Full name</Label>
                    <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                      className="mt-1" data-testid="bc-name-input" />
                  </div>
                  <div>
                    <Label>Title / Position</Label>
                    <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                      className="mt-1" data-testid="bc-title-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Company</Label>
                    <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
                      className="mt-1" data-testid="bc-company-input" />
                  </div>
                  <div>
                    <Label>Classification</Label>
                    <Select value={form.classification} onValueChange={(v) => setForm({ ...form, classification: v })}>
                      <SelectTrigger className="mt-1" data-testid="bc-classification-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CLASSIFICATIONS.map((c) => (<SelectItem key={c} value={c}>{c.replaceAll("_", " ")}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Country</Label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="mt-1" /></div>
                  <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1" /></div>
                  <div><Label>Industry</Label><Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} className="mt-1" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="bc-email-input" /></div>
                  <div><Label>Website</Label><Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="mt-1" /></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" /></div>
                  <div><Label>Mobile</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="mt-1" /></div>
                  <div><Label>WhatsApp</Label><Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} className="mt-1" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Event name</Label><Input value={form.event_name} onChange={(e) => setForm({ ...form, event_name: e.target.value })} placeholder="Uganda Trade Expo 2026" className="mt-1" /></div>
                  <div><Label>Meeting location</Label><Input value={form.meeting_location} onChange={(e) => setForm({ ...form, meeting_location: e.target.value })} className="mt-1" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Meeting date</Label><Input type="date" value={form.meeting_date} onChange={(e) => setForm({ ...form, meeting_date: e.target.value })} className="mt-1" /></div>
                  <div><Label>Product / Service category</Label><Input value={form.product_category} onChange={(e) => setForm({ ...form, product_category: e.target.value })} className="mt-1" /></div>
                </div>
                <div>
                  <Label>Strategic notes / Partnership areas</Label>
                  <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" />
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={busy}
                    className="bg-emerald-900 hover:bg-emerald-950 text-white"
                    data-testid="bc-submit-button">
                    {busy ? "Saving…" : "Save contact"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="bg-white border border-gray-200 rounded-md p-3 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Name, company, country, industry…" value={q}
            onChange={(e) => setQ(e.target.value)} className="pl-8 h-9"
            data-testid="bc-search-input" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-52 h-9" data-testid="bc-filter-classification">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All classifications</SelectItem>
            {CLASSIFICATIONS.map((c) => (<SelectItem key={c} value={c}>{c.replaceAll("_", " ")}</SelectItem>))}
          </SelectContent>
        </Select>
        <span className="text-xs text-gray-500">{filtered.length} contact(s)</span>
      </div>

      {loading ? (
        <div className="p-10 text-center text-sm text-gray-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <EmptyState title="No business cards yet"
          hint="Tap “Add contact”, scan a business card and watch GPT-5.2 vision auto-fill the form." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="bc-grid">
          {filtered.map((c) => (
            <div key={c.id} className="bg-white border border-gray-200 rounded-md overflow-hidden hover:shadow-md transition-shadow">
              {c.image_file_id && (
                <img src={fileUrl(c.image_file_id)} alt="card"
                  className="w-full h-32 object-cover bg-amber-50" />
              )}
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-display font-semibold text-gray-900 truncate">{c.full_name || "(no name)"}</div>
                    <div className="text-xs text-gray-600 truncate">{c.title}</div>
                    <div className="font-medium text-sm text-emerald-900 mt-1 truncate">{c.company}</div>
                  </div>
                  <Badge tone="amber">{(c.classification || "").replaceAll("_", " ")}</Badge>
                </div>
                <div className="text-xs text-gray-600 space-y-1 mt-3">
                  {c.email && <div className="flex items-center gap-1.5 truncate"><Mail size={11} /> {c.email}</div>}
                  {(c.mobile || c.phone) && <div className="flex items-center gap-1.5"><Phone size={11} /> {c.mobile || c.phone}</div>}
                  {c.website && <div className="flex items-center gap-1.5 truncate"><Globe size={11} /> {c.website}</div>}
                  {(c.city || c.country) && <div className="flex items-center gap-1.5 truncate"><MapPin size={11} /> {[c.city, c.country].filter(Boolean).join(", ")}</div>}
                </div>
                {c.event_name && (
                  <div className="mt-3 text-[10px] uppercase tracking-wider text-gray-500 border-t border-gray-100 pt-2">
                    Met at: <span className="text-gray-700 normal-case">{c.event_name}</span>
                  </div>
                )}
                <div className="flex justify-end mt-2">
                  <Button variant="ghost" size="sm" onClick={() => remove(c.id)}
                    className="text-red-700 hover:bg-red-50" data-testid={`bc-delete-${c.id}`}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
